import { Box, Button, Icon, SxProps, Typography } from "@outschool/backpack";
import { Search as SearchFilters } from "@outschool/business-rules";
import { SearchListingsQueryVariables } from "@outschool/gql-frontend-generated";
import { faArrowUpArrowDown } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { useSearchFiltersString } from "@outschool/ui-components-website";
import { useImpressionTracking } from "@outschool/ui-legacy-component-library";
import { Screen } from "@outschool/ui-utils";
import { useSelect } from "downshift";
import React from "react";

import { SetFiltersType } from "../../lib/useSearchFilters";

type SearchFilters = SearchListingsQueryVariables["searchFilters"];

interface ListingsSortByProps {
  filters: SearchFilters;
  setFilters?: SetFiltersType;
  sx?: SxProps;
  iconSx?: SxProps;
}

interface OrderOption {
  label: string;
  value: string;
}

export default function ListingsSortBy({
  filters,
  setFilters,
  sx,
  iconSx,
}: ListingsSortByProps) {
  const [node, setNode] = React.useState<HTMLElement | null>(null);
  const { t } = useTranslation("client\\components\\search\\ListingsSortBy");
  const { ORDER_OPTIONS } = useSearchFiltersString();
  const { trackTouch: trackSortChange } = useImpressionTracking({
    node,
    trackingLabel: "sortOrderChanged",
    uniqueId: "sortOrderChanged",
  });

  const isGroupOnlySearch =
    filters?.delivery?.split(",")?.length === 1 &&
    filters?.delivery === "Group";

  const orderOptions = ORDER_OPTIONS;
  const options = isGroupOnlySearch
    ? orderOptions.filter((option: OrderOption) => option.value !== "upcoming")
    : orderOptions;
  let defaultOrder = "relevance";

  const defaultLabel = options.filter(
    (option: OrderOption) => option.value === defaultOrder,
  )[0]?.label;

  const isMobile = Screen.useIsMobile();

  const {
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
  } = useSelect({
    items: options,
    onSelectedItemChange: ({ selectedItem }) => {
      const order = selectedItem?.value;
      trackSortChange({
        orderAfter: order,
        orderBefore: filters.order,
      });
      setFilters?.(t("Order"), { order });
    },
  });

  return (
    <Box>
      <Button
        size="small"
        sx={{
          flex: 1,
          width: "64px",
          fontWeight: "fontWeightMedium",
          ...(isMobile && {
            color: "primary.600",
            borderColor: "primary.600",
            textAlign: "left",
            paddingX: "1em",
            height: "40px",
            fontSize: 16,
            "&:hover:not(:disabled)": {
              color: "primary.600",
              borderColor: "primary.600",
              backgroundColor: "common.white",
            },

            "&:focus": {
              color: "#6161F2 !important",
              borderColor: "#6161F2 !important",
              backgroundColor: "white !important",
            },
          }),
          ...(!isMobile && {
            color: "neutral.900",
            borderColor: "grey.300",
          }),
          ...sx,
        }}
        {...getToggleButtonProps()}
      >
        <Icon
          icon={faArrowUpArrowDown}
          sx={{
            color: isMobile ? "primary.600" : "neutral.900",
            // dictates mobile icon size - not related to actual font size
            fontSize: isMobile ? "20px" : "14px",
            ...iconSx,
          }}
        />
      </Button>
      <ul
        {...getMenuProps({
          style: {
            backgroundColor: "white",
            boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.25)",
            borderRadius: 8,
            marginTop: "5px",
            padding: isOpen ? 8 : 0,
            width: isMobile ? "140px" : "180px",
            position: "absolute",
          },
          ref: setNode,
        })}
      >
        {isOpen &&
          options.map((option: OrderOption, index: number) => (
            <li
              key={option.value}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "left",
                padding: "16px",
                backgroundColor:
                  highlightedIndex === index
                    ? "#ccd4ff"
                    : (selectedItem ? selectedItem.label : defaultLabel) ===
                      option.label
                    ? "#E6E9FF"
                    : "initial",
                borderBottom:
                  index + 1 !== options.length
                    ? "1px solid rgba(221, 223, 227, 1)"
                    : "initial",
                cursor: "pointer",
              }}
              {...(getItemProps({ item: option, index }) as object)}
            >
              <Typography
                variant={isMobile ? "subtitle2" : "inherit"}
                sx={{
                  color: isMobile ? "primary.600" : "neutral.900",
                }}
              >
                {option.label}
              </Typography>
            </li>
          ))}
      </ul>
    </Box>
  );
}
